import { Injectable } from '@angular/core';
import { UserReview } from 'shared/src/components/user-review/user-review.component';
import { KindlePaywallService } from '../../../../kindle-funnel/src/app/services/kindle-paywall.service';
import { highlightText } from '../../../../funnel/src/app/data/quiz';
import {
  PaywallAudioTrackSection,
  PaywallFaq,
  PaywallFeature,
} from '../../../../funnel/src/app/services/paywall.service';
import { getRomanceBookIdsForSubgenres, romanceFeaturedBookIds } from '../data/romance-books';
import { RomanceFunnelFlowService } from './romance-funnel-flow.service';

declare const $localize: any;

const maxRomanceShelfSamples = 4;

@Injectable({
  providedIn: 'root',
})
export class RomancePaywallService extends KindlePaywallService {
  // Without this override, KindlePaywallService.getTitle() would show
  // "Kindle listening plan" on the romance paywall.
  override getTitle(): string {
    return (
      $localize`:@@romancePaywallTitlePrefix:Your ` +
      highlightText($localize`:@@romancePaywallTitleHighlight:listening plan`) +
      $localize`:@@romancePaywallTitleSuffix: is ready`
    );
  }

  // Full replacement of the kindle sections (no super call): the kindle
  // implementation always renders a non-fiction section - with no
  // nonFictionInterests answer it falls back to default non-fiction titles
  // plus motivation-mapped books (all non-fiction) under a "picks from your
  // shelf" heading. This funnel dropped that question, so the paywall
  // samples two romance-only sections instead: subgenre picks (falling
  // back to the featured pool), then a "what others are playing" section
  // from the featured pool topping up the remaining slots.
  override getAudioTrackSections(): PaywallAudioTrackSection[] {
    // flowService is injected under the base FunnelFlowService token (see
    // app.config.ts), which resolves to this singleton RomanceFunnelFlowService
    // instance at runtime - cast, don't re-type the constructor, or DI would
    // hand this service a second, unrelated instance with no quiz answers.
    const romanceFlowService = this.flowService as RomanceFunnelFlowService;
    const subgenreIds = romanceFlowService.getRomanceSubgenreIds();
    const subgenreBookIds = getRomanceBookIdsForSubgenres(subgenreIds);
    const shelfBookIds = this.takeUniqueBookIds(
      subgenreBookIds.length > 0 ? subgenreBookIds : romanceFeaturedBookIds,
      maxRomanceShelfSamples
    );
    const trendingBookIds = this.takeUniqueBookIds(
      romanceFeaturedBookIds,
      this.getRemainingSampleLimit(shelfBookIds.length),
      shelfBookIds
    );

    const sections = [
      {
        title: $localize`:@@romanceSamplesShelf:🌹 Picks from your shelf`,
        tracks: this.sampleContentService.getBooks(shelfBookIds),
      },
      {
        title: $localize`:@@romanceSamplesTrending:🌶️ What other romance listeners are playing`,
        tracks: this.sampleContentService.getBooks(trendingBookIds),
      },
    ];

    return sections.filter(section => section.tracks.length > 0);
  }

  override getContextualTextBundle(): { title: string; beforeText: string; afterText: string } {
    return {
      title: $localize`:@@romanceContextTitle:Reading Flow`,
      beforeText: $localize`:@@romanceContextBefore:Stories waiting unread`,
      afterText: $localize`:@@romanceContextAfter:Books finished in the moments you already have`,
    };
  }

  override getReviews(): UserReview[] {
    return [
      {
        name: $localize`:@@romanceReviewName1:Carol Jennings`,
        age: 57,
        message: $localize`:@@romanceReviewMessage1:My Kindle was full of books I never got to. Now they play while I fold laundry and water the garden, and I finished four last month.`,
        avatarUrl: 'assets/img/avatars/w1.jpg',
        rate: 4.9,
      },
      {
        name: $localize`:@@romanceReviewName2:Mark Delgado`,
        age: 61,
        message: $localize`:@@romanceReviewMessage2:I didn't think a voice reading to me would hold my attention. Now it's on every drive home, and traffic stopped bothering me. Two series done since spring.`,
        avatarUrl: 'assets/img/avatars/m1.jpg',
        rate: 4.8,
      },
      {
        name: $localize`:@@romanceReviewName3:Patricia Nguyen`,
        age: 52,
        message: $localize`:@@romanceReviewMessage3:I used to read romance in bed until my eyes gave out. Now I put a chapter on with the lights off, and I'm asleep before the narrator is.`,
        avatarUrl: 'assets/img/avatars/w4.jpg',
        rate: 4.9,
      },
    ];
  }

  override getPlanCta(): string {
    return $localize`:@@romancePaywallPlanCta:Start your plan while your listener discount is active`;
  }

  override getHighlights(): string[] {
    return [
      $localize`:@@romancePaywallHighlight1:Turn the books on your Kindle into beautiful audio`,
      $localize`:@@romancePaywallHighlight2:Listen while gardening, cooking, walking, or winding down`,
      $localize`:@@romancePaywallHighlight3:Warm, natural voices that do your stories justice`,
      $localize`:@@romancePaywallHighlight4:Rest your eyes without losing your place in the book`,
      $localize`:@@romancePaywallHighlight5:A listening routine built around the books you already chose`,
    ];
  }

  override getFeatures(): PaywallFeature[] {
    return [
      {
        emoji: '📚',
        title: $localize`:@@romanceFeatureLibraryTitle:Convert your Kindle library into audiobooks`,
        description: $localize`:@@romanceFeatureLibraryDesc:Turn the books you already own into natural-sounding audio in a few taps - no second purchase needed.`,
      },
      {
        emoji: '💝',
        title: $localize`:@@romanceFeatureVoicesTitle:Voices made for stories`,
        description: $localize`:@@romanceFeatureVoicesDesc:Warm narration that carries emotion through every chapter - from slow-burn romance to book-club favorites.`,
      },
      {
        emoji: '🌷',
        title: $localize`:@@romanceFeatureHandsFreeTitle:Listen while your hands are busy`,
        description: $localize`:@@romanceFeatureHandsFreeDesc:Keep the story going while you garden, cook, tidy up, or take your morning walk.`,
      },
      {
        emoji: '👀',
        title: $localize`:@@romanceFeatureEyesTitle:Give your eyes a break`,
        description: $localize`:@@romanceFeatureEyesDesc:When small print and evening reading feel tiring, switch to listening and stay in the story.`,
      },
      {
        emoji: '⏩',
        title: $localize`:@@romanceFeaturePaceTitle:Set your own pace`,
        description: $localize`:@@romanceFeaturePaceDesc:Slow down to savor the big moments or speed up through familiar chapters without losing the thread.`,
      },
      {
        emoji: '✅',
        title: $localize`:@@romanceFeatureFinishTitle:Built for finishing`,
        description: $localize`:@@romanceFeatureFinishDesc:Turn the quiet pockets of your day into real progress on the books waiting in your library.`,
      },
    ];
  }

  // The kindle FAQ pitches "fiction and non-fiction" listening; this funnel
  // is romance-only, so the two content answers come from the approved
  // romance copy while the Kindle/Audible and account/access items stay
  // shared.
  override getFaqs(): PaywallFaq[] {
    return [
      {
        question: $localize`:@@kindleFaqUseKindleQuestion:Can I use Peech for books from my Kindle library?`,
        answer: $localize`:@@kindleFaqUseKindleAnswer:Peech turns readable text and supported files you provide into audio. Some Kindle purchases are protected by publisher restrictions, so availability depends on whether you can access the text in a supported format.`,
      },
      {
        question: $localize`:@@kindleFaqAudibleQuestion:Is this the same as Audible?`,
        answer: $localize`:@@kindleFaqAudibleAnswer:No. Audible sells finished audiobooks. Peech helps you listen to text you already have, including supported ebooks, documents, links, notes, and long reads.`,
      },
      {
        question: $localize`:@@romanceFaqLongBooksQuestion:Will this work for a 600-page romantasy?`,
        answer: $localize`:@@romanceFaqLongBooksAnswer:Yes. Peech is built for long books and whole series, and it never loses your spot between chapters.`,
      },
      {
        question: $localize`:@@romanceFaqPrivateQuestion:Can I listen to the spicy ones without anyone knowing?`,
        answer: $localize`:@@romanceFaqPrivateAnswer:Yes. Headphones in, and it's just you and the narrator.`,
      },
      {
        question: $localize`:@@faqGetAccess:How do I get access to the app?`,
        answer: $localize`:@@faqGetAccessAnswer:After you fill in your email address, a mail with app download link will be in your inbox. Just install the app, Sign In with your email.`,
      },
      {
        question: $localize`:@@faqCancelAccount:How do I cancel my account?`,
        answer: $localize`:@@faqCancelAccountAnswer:It's easy! Just log into your account here, and click the Cancel button.`,
      },
    ];
  }

  override getSocialProofText(totalUsersThisHour: number): string {
    return $localize`:@@romanceListenersStartedLastHour:${totalUsersThisHour}:INTERPOLATION: romance listeners started their plan in the last hour`;
  }

  override getCommunityTitle(): string {
    return (
      $localize`:@@romanceCommunityTitlePrefix:You're ` +
      highlightText($localize`:@@romanceCommunityTitleHighlight:one of us`) +
      $localize`:@@romanceCommunityTitleSuffix: now`
    );
  }
}
